import React from 'react';
import axios from 'axios';
import omit from 'lodash/omit';
import { withRouter } from 'react-router-dom';
import { lawsAPIToken } from '~api/laws';
import { toast } from 'react-toastify';
import { ErrorMessage } from '~components/toast';
import { withNamespaces, Trans } from 'react-i18next';
import Input from '~components/input';
import LoadingButton from '~components/loading-button';
import { iconCheckBold } from '~utils/svgIcons';
import { identifyUserAnalytics } from '~utils/analytics';
import '~routes/login-page/style.scss';
import './style.scss';

function ResetForm({ history, logIn, isFetching, isMobile, redirectPath }) {
    const token = React.useMemo(() => {
        const pageParams = new URLSearchParams(document.location.search.substring(1));
        if (pageParams instanceof Object) {
            return pageParams.get('token') || '';
        }
        return '';
    }, []);

    const [isLoading, setLoading] = React.useState(false);

    const [tokenError, setTokenError] = React.useState('');

    const [state, setState] = React.useState({
        password: {
            value: '',
            isInvalid: false,
            error: <Trans i18nKey="validationRules.required-message" />,
        },
        secondpassword: {
            value: '',
            isInvalid: false,
            error: <Trans i18nKey="validationRules.required-message" />,
        },
    });

    const [validationState, setValidationState] = React.useState({
        length: false,
        number: false,
        uppercaseChar: false,
        lowercaseChar: false,
        special: false,
    });

    const validatePassword = React.useCallback((value) => {
        const length = value.length > 7;
        const number = value.search(/[0-9]{1}/gi) !== -1;
        const uppercaseChar = value.search(/[A-Z]{1}/g) !== -1;
        const lowercaseChar = value.search(/[a-z]{1}/g) !== -1;
        const special = value.search(/[$&+,:;=?@#|'<>.^*()%!-]{1}/gi) !== -1;

        setValidationState({
            length,
            number,
            uppercaseChar,
            lowercaseChar,
            special,
        });
    }, []);

    const onChangeHandler = React.useCallback(
        (event) => {
            setState({
                ...state,
                [event.target.name]: {
                    value: event.target.value.trim(),
                    isInvalid: false,
                    error: '',
                },
            });
            if (event.target.name === 'password') {
                validatePassword(event.target.value);
            }
        },
        [state, validatePassword]
    );

    React.useEffect(() => {
        lawsAPIToken()
            .post('/users/check_password_recovery_token/', { token: token })
            .then(() => setTokenError(''))
            .catch((error) => {
                if (!axios.isCancel(error)) {
                    if (error.response.status === 400 && error.response.data === 'invalid token')
                        setTokenError(
                            <p>
                                Ссылка, по которой Вы перешли, устарела.
                                <br />
                                Необходимо отправить повторный запрос для восстановления пароля
                            </p>
                        );
                    else setTokenError(error.message);
                }
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onFormSubmit = React.useCallback(
        async (event) => {
            event.preventDefault();
            const { password, secondpassword } = state;

            let isValid = true;
            for (let k in validationState) {
                if (!validationState[k]) {
                    isValid = false;
                    break;
                }
            }

            const isPasswordEmpty = !password.value;
            const isSecondPasswordEmpty = !secondpassword.value;

            const isPasswordsTheSame = password.value === secondpassword.value;

            if (!isValid || !isPasswordsTheSame) {
                setState({
                    password: {
                        value: password.value.trim(),
                        isInvalid: !isValid || isPasswordEmpty ? true : false,
                        error: isPasswordEmpty ? (
                            <Trans i18nKey="validationRules.required-message" />
                        ) : !isValid ? (
                            'Пароль не соответствует требованиям'
                        ) : (
                            ''
                        ),
                    },
                    secondpassword: {
                        value: secondpassword.value.trim(),
                        isInvalid: !isPasswordsTheSame || isSecondPasswordEmpty ? true : false,
                        error: !isPasswordsTheSame ? (
                            'Пароли не совпадают'
                        ) : isSecondPasswordEmpty ? (
                            <Trans i18nKey="validationRules.required-message" />
                        ) : (
                            ''
                        ),
                    },
                });
                return;
            }

            setLoading(true);

            const lawsResponse = await lawsAPIToken()
                .post('/users/reset_password/', { token: token, password: secondpassword.value })
                .then((data) => data)
                .catch((error) => {
                    console.log(error);
                    if (!axios.isCancel(error)) {
                        toast(<ErrorMessage message={error.message} />);
                    }
                    setLoading(false);
                    return false;
                });

            let user = {};
            if (lawsResponse && lawsResponse.data) {
                const group = Array.isArray(lawsResponse.data.user.groups) ? lawsResponse.data.user.groups[0] : null;
                const listEcosystemFunctions = group &&
                    Array.isArray(group.ecosystem_functions) && [...group.ecosystem_functions];
                let ecosystemFunctions = {};
                Array.isArray(listEcosystemFunctions) &&
                    listEcosystemFunctions.map((item) => (ecosystemFunctions[item.code] = true));
                const company = group ? group.company : null;
                const listSubdivisions = company ? company.groups : null;
                user = {
                    ...user,
                    ...omit(lawsResponse.data.user, ['id', 'groups']),
                    group: Array.isArray(lawsResponse.data.user.groups) ? lawsResponse.data.user.groups[0] : null,
                    ecosystem: {
                        functions: ecosystemFunctions,
                        listFunctions: listEcosystemFunctions,
                        company,
                        listSubdivisions: listSubdivisions || null,
                    },
                    lawsUser: {
                        id: lawsResponse.data.user.id,
                        token: lawsResponse.data.token,
                        token_expires_at: lawsResponse.data.token_expires_at,
                    },
                };
            }
            const pathBeforeLogin = window.location.pathname;
            if (user.id) {
                window.ym &&
                    window.ym(73097359, 'userParams', {
                        UserID: `${user.id}`,
                        username: user.username,
                        group_name: user.group_name,
                    });

                identifyUserAnalytics(user);
                console.log('setted logged user');
                const isUserPath = pathBeforeLogin.length > 2;
                history.push(isUserPath ? pathBeforeLogin : redirectPath);
            }
            logIn(user);
        },
        [state, token, validationState, logIn, history, redirectPath]
    );

    const { password, secondpassword } = state;

    return (
        <form className="new-password-form">
            <h3 className="login-page__header">{tokenError || 'Сброс пароля'}</h3>
            {!tokenError && (
                <React.Fragment>
                    <p className="new-password-form__description">Новый пароль должен содержать не менее:</p>
                    <ul className="new-password-form__checklist">
                        <li className="new-password-form__check-item">
                            <span className={`new-password-form__check${validationState.length ? ' active' : ''}`}>
                                <i className="new-password-form__check-icon">{iconCheckBold}</i>
                            </span>{' '}
                            8 знаков/символов
                        </li>
                        <li className="new-password-form__check-item">
                            <span className={`new-password-form__check${validationState.number ? ' active' : ''}`}>
                                <i className="new-password-form__check-icon">{iconCheckBold}</i>
                            </span>{' '}
                            1 цифра
                        </li>
                        <li className="new-password-form__check-item">
                            <span
                                className={`new-password-form__check${validationState.uppercaseChar ? ' active' : ''}`}
                            >
                                <i className="new-password-form__check-icon">{iconCheckBold}</i>
                            </span>{' '}
                            1 заглавная
                        </li>
                        <li className="new-password-form__check-item">
                            <span
                                className={`new-password-form__check${validationState.lowercaseChar ? ' active' : ''}`}
                            >
                                <i className="new-password-form__check-icon">{iconCheckBold}</i>
                            </span>{' '}
                            1 строчная
                        </li>
                        <li className="new-password-form__check-item">
                            <span className={`new-password-form__check${validationState.special ? ' active' : ''}`}>
                                <i className="new-password-form__check-icon">{iconCheckBold}</i>
                            </span>{' '}
                            1 спецсимвол
                        </li>
                    </ul>
                    <Input
                        type="password"
                        placeholder="Введите новый пароль"
                        name="password"
                        onChange={onChangeHandler}
                        value={password.value}
                        isError={password.isInvalid}
                        message={password.error}
                    />
                    <Input
                        type="password"
                        placeholder="Повторите новый пароль"
                        name="secondpassword"
                        onChange={onChangeHandler}
                        value={secondpassword.value}
                        isError={secondpassword.isInvalid}
                        message={secondpassword.error}
                    />
                    <LoadingButton
                        type="submit"
                        size={isMobile ? 'small' : 'big'}
                        disabled={isLoading}
                        onClick={onFormSubmit}
                        isLoading={isLoading}
                        isMobile={isMobile}
                        ariaLabel="Восстановить пароль"
                    >
                        Изменить пароль
                    </LoadingButton>
                </React.Fragment>
            )}
        </form>
    );
}

export default withRouter(withNamespaces('translation')(ResetForm));



// WEBPACK FOOTER //
// ./src/routes/login-page/login/new-password-form/index.js